import { useCallback, useMemo } from "react";

import { useDispatch, useSelector } from "react-redux";

import { Entity, Id } from "@/jenesys-types/data/common";
import { DataType } from "@/jenesys-types/data/enums";

import { PathParams } from "@/services/api";
import { selectDeleteState } from "@/store/crud";
import {
  deleteItem as deleteItemAction,
  deleteItems as deleteItemsAction
} from "@/store/crud/delete";
import { NotificationDefinition } from "@/store/store";

const useEntityDelete = (
  dataType: DataType,
  returnPath?: string,
  pathParams?: PathParams
) => {
  const dispatch = useDispatch();

  // Memoize the selector otherwise it will be new function per render.
  const deleteStateSelector = useMemo(
    () => selectDeleteState(dataType),
    [dataType]
  );
  const deleteState = useSelector(deleteStateSelector);

  const deleteItem = useCallback(
    (item: Entity) => {
      const successNotification: NotificationDefinition = {
        type: "success",
        message: `${dataType} has been deleted successfully`,
        removeAfter: 5000
      };
      const failureNotification: NotificationDefinition = {
        type: "error",
        message: `${dataType} has not been deleted successfully`
      };
      dispatch(
        deleteItemAction(
          dataType,
          item.id,
          successNotification,
          failureNotification,
          returnPath,
          pathParams
        )
      );
    },
    [dispatch, dataType, returnPath, pathParams]
  );

  const deleteItems = useCallback(
    (ids: Array<Id>) => {
      const successNotification: NotificationDefinition = {
        type: "success",
        message: `multiple ${dataType}'s have been deleted successfully`,
        removeAfter: 5000
      };
      const failureNotification: NotificationDefinition = {
        type: "error",
        message: `multiple ${dataType}'s have not been deleted successfully`
      };
      dispatch(
        deleteItemsAction(
          dataType,
          ids,
          successNotification,
          failureNotification
        )
      );
    },
    [dispatch, dataType]
  );

  return {
    isLoading: !!deleteState?.isLoading,
    error: deleteState?.error,
    deleteItem,
    deleteItems
  };
};

export default useEntityDelete;
